import React, { useState, useEffect } from "react";
import {
  DialogActions,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Checkbox,
  FormControlLabel,
  Collapse,
  Typography,
} from "@material-ui/core";
import { FilePond } from "react-filepond";
import "filepond/dist/filepond.min.css";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import UploadError from "../../messages/UploadError";
const displayableMimeTypes = [
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-word.document.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint.presentation.macroEnabled.12",
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow",
  "application/vnd.ms-powerpoint.slideshow.macroEnabled.12",
  "application/msword",
  "application/vnd.ms-word",
  "application/vnd.ms-powerpoint",
  "application/powerpoint",
  "application/x-powerpoint",
  "application/pdf",
];
function AttachFileDialog(props) {
  const [downloadLink, setDownloadLink] = useState(false);
  const [title, setTitle] = useState("");
  const [files, setFiles] = useState([]);
  const [attachmentId, setAttachmentId] = useState(null);
  const [fileName, setFileName] = useState("");
  const [attachmentContentType, setAttachmentContentType] = useState("");
  const [uploadError, setUploadError] = useState(false);
  const { t } = useTranslation();
  const apiLocation = useSelector((state) => state.setting.apiLocation);

  let isNotDisplayable =
    attachmentContentType &&
    !displayableMimeTypes.includes(attachmentContentType);

  const getTransKeyFromHttpStatus = (status, message) =>{
    let translationKey = "unexpected_upload_error";
    switch(status){
      case 409:
        translationKey="virus_detected_in_file"
        break;
      case 413:
        translationKey="file_too_big_to_process"
        break;
      case 418: 
        // I'm a teapot. 
        // Temporary method to handle internal 500 errors. See UV6-1207 
        // Error type is propagated as a translation key
        // First characters are the Http status it prepresents
        // e.g. 500_AV_NOT_DEFINED
        if(!!message && message.length>0){
          translationKey = message;
        }
    }
    return translationKey;
  }



  return (
    <Dialog
      style={{ zIndex: 10001 }}
      fullWidth
      open={props.open}
      onClose={props.onClose}
      PaperProps={{ style: { overflow: "inherit" } }}
    >
      <DialogTitle>Attach a file</DialogTitle>
      <DialogContent>
        <FormControlLabel
          style={{ marginBottom: 5 }}
          label="Attach as a download link"
          control={
            <Checkbox
              checked={downloadLink}
              disabled={isNotDisplayable}
              onChange={(e) => setDownloadLink(e.target.checked)}
              color="primary"
            />
          }
        />
        <Collapse in={downloadLink}>
          <TextField
            aria-label="Link label"
            fullWidth
            label="Display text"
            value={title}
            required
            error={downloadLink && !title}
            helperText={downloadLink && !title ? "Required" : ""}
            onChange={(e) => setTitle(e.target.value)}
            style={{ marginBottom: 15 }}
          />
        </Collapse>
        <FilePond
          files={files}
          allowMultiple={false}
          allowRevert={false}
          server={{
            process: (
              fieldName,
              file,
              metadata,
              load,
              error,
              progress,
              abort,
              transfer,
              options
            ) => {
              let timeout = null;
              const formData = new FormData();
              formData.append("file", file, file.name);
              formData.append(
                "contentAttachmentDTO",
                JSON.stringify({
                  convertOfficeToPdf: !downloadLink,
                })
              );
              const request = new XMLHttpRequest();
              request.withCredentials = true
              request.open(
                "POST",
                `https://${apiLocation}/attachmentservice/attachment/upload/`
              );
              request.upload.onprogress = (e) => {
                // cap it at 90% to allow for processing time...
                progress(e.lengthComputable, e.loaded * 0.9, e.total);

                if (e.loaded === e.total) {
                  // fully uploaded, fake some progress while we're processing
                  let processingProgress = 90;
                  const tick = () => {
                    if (processingProgress === 99) return;
                    progress(true, ++processingProgress, 100);
                    let delay = 250 + Math.random() * 225;
                    timeout = setTimeout(tick, delay);
                  };
                  tick();
                }
              };
              request.onload = function() {
                let payload = JSON.parse(request.responseText);
                if (request.status >= 200 && request.status < 300) {
                  clearTimeout(timeout);
                  progress(true, 1, 1);
                  load(payload.id);
                  props.updateUploadArray &&
                    props.updateUploadArray(
                      payload.id
                    );
                } else{
                  let key = getTransKeyFromHttpStatus(request.status, payload.message);
                  error(t(key));
                  setUploadError(key);
                }
              };
              request.send(formData);
              return {
                abort: () => {
                  request.abort();
                  abort();
                },
              };
            },
          }}
          onprocessfile={(_err, file) => {
            if(!_err){
              setAttachmentId(file.serverId);
              setAttachmentContentType(file.file.type);
              if (!displayableMimeTypes.includes(file.file.type)) {
                setDownloadLink(true);
              }
              setFileName(file.file.name);
            }
          }}
          onupdatefiles={(fileItems) => {
            setFiles(fileItems.map((fileItem) => fileItem.file));
          }}
        />
        {isNotDisplayable && (
          <Typography variant="body2">
            This file type cannot be displayed in the editor. Please provide
            display text to create a download link.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} color="default">
          {t("global_cancel")}
        </Button>
        <Button
          aria-label="Insert attachment"
          disabled={!attachmentId || (downloadLink && !title)}
          onClick={() => {
            props.onSubmit(attachmentId, attachmentContentType, title);
          }}
          color="default"
        >
          {t("global_insert")}
        </Button>
      </DialogActions>
      <UploadError
        uploadError={uploadError}
        setUploadError={() => setUploadError(false)}
      />
    </Dialog>
  );
}

export default AttachFileDialog;
